import { API_URL } from '../../../../constants/urls.constants';
import type { GetQueryArgsType } from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import { invalidateOnSuccess } from '../../../app.utils';
import type {
  BulkRedlistApplicantBody,
  BulkRedlistApplicantResponse,
  GetAllOfferedApplicantsParams,
  GetAllOfferedApplicantsResponse,
  GetOfferLetterDetailsResponse,
  GetRedlistedApplicantsParams,
  GetRedlistedApplicantsResponse,
} from './hiring-pipeline.type';

const { OFFER_LETTERS, HIRING_PIPELINE, ASSESSMENTS, EVALUATED_INTERVIEWS } =
  APP_API_TAGS || {};

export const hiringPipelineApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST: Send offer letter to an applicant
    sendOfferLetter: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.recruitmentOfferLetter,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([
        ASSESSMENTS,
        OFFER_LETTERS,
        EVALUATED_INTERVIEWS,
      ]),
    }),

    // PATCH: Bulk update hiring pipeline status
    updateHiringPipelineStatus: builder.mutation<any, any>({
      query: (body) => ({
        url: API_URL.recruitmentBulkHiringStatus,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([OFFER_LETTERS, HIRING_PIPELINE]),
    }),

    // GET: All offered applicants with filters
    getAllOfferedApplicants: builder.query<
      GetAllOfferedApplicantsResponse,
      GetQueryArgsType<GetAllOfferedApplicantsParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.recruitmentOfferLetter,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [OFFER_LETTERS],
    }),

    // GET: Offer letter details by ID
    getOfferLetterDetails: builder.query<GetOfferLetterDetailsResponse, string>(
      {
        query: (offer_letter_id) => ({
          url: `${API_URL.recruitmentOfferLetter}/${offer_letter_id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [
          { type: OFFER_LETTERS as any, id },
        ],
      },
    ),

    // GET: All red-listed applicants
    getRedlistedApplicants: builder.query<
      GetRedlistedApplicantsResponse,
      GetQueryArgsType<GetRedlistedApplicantsParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.recruitmentApplicantRedList,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [HIRING_PIPELINE],
    }),

    // POST: Bulk red-list applicants
    bulkRedlistApplicant: builder.mutation<
      BulkRedlistApplicantResponse,
      BulkRedlistApplicantBody
    >({
      query: (body) => ({
        url: `${API_URL.recruitmentApplicantRedList}/bulk`,
        method: 'POST',
        body,
      }),
      invalidatesTags: invalidateOnSuccess([HIRING_PIPELINE]),
    }),
  }),
});

export const {
  useSendOfferLetterMutation,
  useUpdateHiringPipelineStatusMutation,
  useGetAllOfferedApplicantsQuery,
  useGetOfferLetterDetailsQuery,
  useGetRedlistedApplicantsQuery,
  useBulkRedlistApplicantMutation,
} = hiringPipelineApi;
